import React, { useState } from 'react';
import { FilterBar } from '../components/FilterBar';
import { areaOfStudyData } from '../data/areaOfStudyData';
import { getDefaultProgram, areaStudyOptions } from '../data/filterOptions';
import { articles, Article } from '../data/articlesData';
import { useMetaTags } from '../hooks/useMetaTags';
import { ClockIcon, UserIcon, CalendarIcon, ArrowRightIcon, BookOpenIcon, SearchIcon } from 'lucide-react';
interface ArticlesPageProps {
  onNavigate: (page: string, articleSlug?: string) => void;
}
export function ArticlesPage({
  onNavigate
}: ArticlesPageProps) {
  const [degreeLevel, setDegreeLevel] = useState<number>(3);
  const [selectedAreaId, setSelectedAreaId] = useState<number>(0); // 0 = all
  const [selectedProgram, setSelectedProgram] = useState<number>(100);
  // Meta tags
  useMetaTags({
    title: 'Articles & Guides for Online Students | DegreeCompare',
    description: 'Expert advice on choosing the right online program, financial aid, career planning, and more. Resources for prospective and current online students.',
    keywords: 'online education articles, college guides, financial aid tips, career planning, online degree advice, online learning resources',
    ogTitle: 'Articles & Guides for Online Students',
    ogDescription: 'Expert advice and resources for choosing the right online degree program, managing financial aid, and planning your career.'
  });
  const handleAreaChange = (areaId: number) => {
    setSelectedAreaId(areaId);
    setSelectedProgram(getDefaultProgram(areaId));
  };
  // Filter articles: 0 = show all, otherwise match areaStudyId or show general (0) articles
  const filteredArticles = selectedAreaId === 0 ? articles : articles.filter(a => a.areaStudyId === selectedAreaId || a.areaStudyId === 0);
  const areaName = selectedAreaId === 0 ? 'All Areas of Study' : areaOfStudyData[selectedAreaId]?.name || 'All Areas';
  return <div data-id="element-1156" data-mp="1rl9piheiuigq">
      {/* Hero */}
      <section className="w-full bg-[#1a2332] text-white py-10 md:py-14 px-4 relative overflow-hidden" data-id="element-1157" data-mp="1rkhtx6kxckqg">
        <div className="absolute top-0 right-0 w-1/2 h-full bg-gradient-to-l from-[#2a374a] to-transparent opacity-50 pointer-events-none" data-id="element-1158" data-mp="1mn83poppo51k"></div>
        <div className="max-w-7xl mx-auto relative z-10" data-id="element-1159" data-mp="20jlao3wmco1s">
          <div className="flex items-center gap-3 mb-4" data-id="element-1160" data-mp="1vm3bh58eln6z">
            <div className="bg-white/10 p-2 rounded-lg" data-id="element-1161" data-mp="2ak5dhazse0m4">
              <BookOpenIcon className="w-6 h-6" data-id="element-1162" data-mp="2aikixldh2ho9" />
            </div>
            <span className="text-sm font-medium text-gray-300 uppercase tracking-wider" data-id="element-1163" data-mp="25lu6d80vm47l">
              Resource Center
            </span>
          </div>
          <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold leading-tight mb-3" data-id="element-1164" data-mp="2jea2rpz9v3zj">
            Articles & Guides
          </h1>
          <p className="text-base text-gray-300 max-w-xl" data-id="element-1165" data-mp="2ta3onzyvs9tz">
            Expert advice on choosing the right program, financial aid, career
            planning, and more.
          </p>
        </div>
      </section>

      {/* Filter Bar */}
      <div className="w-full bg-white border-b border-gray-200 py-4 px-4 shadow-sm relative z-30" data-id="element-1166" data-mp="37xv9zvbllz9f">
        <div className="max-w-7xl mx-auto" data-id="element-1167" data-mp="3cvwvso0escol">
          <div className="flex flex-col sm:flex-row gap-3 w-full" data-id="element-1168" data-mp="2xzegorp6t8k7">
            {/* Custom "All" area selector that includes an "All" option */}
            <div className="flex flex-col sm:flex-row gap-3 w-full" data-id="element-1169" data-mp="32wxxnzd9rzfu">
              <div className="relative flex-1 min-w-0" data-id="element-1170" data-mp="2p6ayy77ie9jr">
                <label className="absolute -top-2 left-3 bg-white px-1.5 text-[10px] font-semibold text-[#3b82f6] z-10 uppercase tracking-wider" data-id="element-1171" data-mp="2p4rfk7qk3idr">
                  Area of Study
                </label>
                <select value={selectedAreaId} onChange={e => handleAreaChange(Number(e.target.value))} className="w-full bg-white border-2 border-gray-200 rounded-lg py-2.5 px-3 text-sm font-medium text-gray-900 focus:outline-none focus:border-[#3b82f6] transition-colors appearance-none cursor-pointer" data-id="element-1172" data-mp="2f6trbk8p5ll5">
                  <option value={0} data-id="element-1173" data-mp="235lqmcrxra2d">All Areas of Study</option>
                  {areaStudyOptions.map(opt => <option key={opt.value} value={opt.value} data-id="element-1174" data-mp="1y9ddf4xatqxu">
                      {opt.label}
                    </option>)}
                </select>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Articles Grid */}
      <div className="max-w-7xl mx-auto px-4 py-8 md:py-10" data-id="element-1175" data-mp="1khgsbku1shay">
        <div className="flex items-center justify-between mb-6" data-id="element-1176" data-mp="1fg9izhc9ba26">
          <h2 className="text-lg font-bold text-gray-900" data-id="element-1177" data-mp="1fj5iha3r66sq">
            {filteredArticles.length} article
            {filteredArticles.length !== 1 ? 's' : ''}
            {selectedAreaId !== 0 && <span className="text-gray-500 font-normal" data-id="element-1178" data-mp="15kzi4wxlh8pz"> in {areaName}</span>}
          </h2>
        </div>

        {filteredArticles.length === 0 ? <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center" data-id="element-1179" data-mp="2zx5tbvqhnpia">
            <SearchIcon className="w-10 h-10 text-gray-300 mx-auto mb-3" data-id="element-1180" data-mp="38cvdz2yqtzfh" />
            <p className="text-gray-600 font-medium" data-id="element-1181" data-mp="3cegkm6kophh8">
              No articles found for this area of study.
            </p>
            <p className="text-sm text-gray-500 mt-1" data-id="element-1182" data-mp="3h79o6r4f2jrq">
              Try selecting a different category or view all articles.
            </p>
          </div> : <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6" data-id="element-1183" data-mp="2pfapvy3sz4vx">
            {filteredArticles.map(article => <ArticleCard key={article.id} article={article} onNavigate={onNavigate} data-id="element-1184" data-mp="2tgw5vsbmvzac" />)}
          </div>}
      </div>
    </div>;
}
function ArticleCard({
  article,
  onNavigate
}: {
  article: Article;
  onNavigate: (page: string, articleSlug?: string) => void;
}) {
  // Generate a color based on area
  const colorMap: Record<number, string> = {
    0: 'bg-gray-100 text-gray-700',
    1: 'bg-blue-50 text-blue-700',
    2: 'bg-red-50 text-red-700',
    3: 'bg-purple-50 text-purple-700',
    4: 'bg-cyan-50 text-cyan-700',
    5: 'bg-green-50 text-green-700',
    6: 'bg-amber-50 text-amber-700',
    7: 'bg-orange-50 text-orange-700',
    8: 'bg-indigo-50 text-indigo-700',
    9: 'bg-gray-100 text-gray-700',
    10: 'bg-teal-50 text-teal-700',
    11: 'bg-pink-50 text-pink-700',
    12: 'bg-violet-50 text-violet-700',
    13: 'bg-yellow-50 text-yellow-700'
  };
  const badgeColor = colorMap[article.areaStudyId] || colorMap[0];
  const handleClick = (e: React.MouseEvent) => {
    e.preventDefault();
    // Every article now has its own detail page, keyed by slug.
    onNavigate('articles', article.slug);
  };
  return <a href={`/articles/${article.slug}`} onClick={handleClick} className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-lg transition-shadow flex flex-col cursor-pointer" data-id="element-1185" data-mp="3bcvj6s7ns81j">
      {/* Color bar top */}
      <div className="h-1.5 bg-[#1a2332] group-hover:bg-[#e8594f] transition-colors" data-id="element-1186" data-mp="y76x8kvsas7t"></div>

      <div className="p-5 flex flex-col flex-grow" data-id="element-1187" data-mp="12yp23k0xhcy2">
        <div className="flex items-center gap-2 mb-3" data-id="element-1188" data-mp="1309up43omlta">
          <span className={`text-[11px] font-semibold px-2 py-0.5 rounded-full ${badgeColor}`} data-id="element-1189" data-mp="oa2d0qli16tn">
            {article.category}
          </span>
        </div>

        <h3 className="text-base font-bold text-gray-900 mb-2 leading-snug group-hover:text-[#3b82f6] transition-colors line-clamp-2" data-id="element-1190" data-mp="1gxgb4s0xo8fc">
          {article.title}
        </h3>

        <p className="text-sm text-gray-600 leading-relaxed mb-4 flex-grow line-clamp-3" data-id="element-1191" data-mp="1luougvxtiii6">
          {article.excerpt}
        </p>

        <div className="flex items-center justify-between pt-3 border-t border-gray-100 mt-auto" data-id="element-1192" data-mp="1bybzzvug80md">
          <div className="flex items-center gap-3 text-xs text-gray-500" data-id="element-1193" data-mp="3smzc3i210s8c">
            <span className="flex items-center gap-1" data-id="element-1194" data-mp="jq71l9tdk5p">
              <CalendarIcon className="w-3 h-3" data-id="element-1195" data-mp="l9m58jqhp5w" />
              {article.date}
            </span>
            <span className="flex items-center gap-1" data-id="element-1196" data-mp="s82pjggx50af">
              <ClockIcon className="w-3 h-3" data-id="element-1197" data-mp="n8g80uu24xvt" />
              {article.readTime}
            </span>
          </div>
          <span className="text-xs font-semibold text-[#e8594f] flex items-center gap-1 group-hover:gap-2 transition-all" data-id="element-1198" data-mp="j5tcaiu9mstc">
            Read <ArrowRightIcon className="w-3 h-3" data-id="element-1199" data-mp="e92xdyu7639i" />
          </span>
        </div>
      </div>
    </a>;
}